import React from "react";
import { Check, Search, Wrench, AlertTriangle } from "lucide-react";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { fmtDur, fmtClock } from "../../workSummaryNew/bits";
import { AppDetail } from "../../workSummaryNew/AppDetail";
import { useApi } from "../../../../../hooks/useApi";
import { useSurface } from "../../growthSurface";
import { inks, paper } from "../viz";
import { HeadlineBlock, KpiStrip, OutcomeCards, Timeline, WaitLine } from "./StoryChrome";

function tokensOf(s) {
  return new Set(String(s || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3));
}
function overlapOf(a, b) {
  const A = tokensOf(a);
  const B = tokensOf(b);
  if (!A.size || !B.size) return 0;
  let n = 0;
  for (const w of A) if (B.has(w)) n += 1;
  return n / Math.min(A.size, B.size);
}
function inferKind(text) {
  const s = String(text || "").toLowerCase();
  if (/still|left|open|missing|unclear|not yet|remain/.test(s)) return "open";
  if (/found|saw|noticed|showed|revealed/.test(s)) return "found";
  if (/decid|chose|picked|went with/.test(s)) return "decided";
  return "did";
}
function insightIcon(kind) {
  if (kind === "found") return Search;
  if (kind === "decided") return Wrench;
  if (kind === "open") return AlertTriangle;
  return Check;
}
function pickWriteupPoints(headline, phases) {
  const seen = [headline];
  const out = [];
  for (const p of phases || []) {
    const text = String(p.what || "").replace(/\s+/g, " ").trim();
    if (!text || seen.some((s) => overlapOf(s, text) >= 0.48)) continue;
    seen.push(text);
    out.push({ text, kind: inferKind(text), who: p.who, time: p.time });
    if (out.length >= 5) break;
  }
  return out;
}

function resolveWorkSummaryApp(apps, name) {
  const n = String(name || "").toLowerCase();
  const list = apps || [];
  const exact = list.find((a) => String(a.name || "").toLowerCase() === n);
  if (exact) return exact.name;
  const part = list.find((a) => {
    const an = String(a.name || "").toLowerCase();
    return an.includes(n) || n.includes(an);
  });
  return part?.name || name;
}

export function WorkAppAnalysis({ t, name, personId, day, onOpenSession }) {
  const api = useApi();
  const sf = useSurface();
  const k = inks(t);
  const [appName, setAppName] = React.useState(null);

  React.useEffect(() => {
    if (!personId || !day) { setAppName(name); return undefined; }
    let alive = true;
    const qs = new URLSearchParams({ userId: personId, date: day, source: "both" });
    api.get(`/api/v1/work-summary-new/day?${qs.toString()}`)
      .then((res) => {
        if (!alive) return;
        setAppName(resolveWorkSummaryApp(res.data?.data?.apps, name));
      })
      .catch(() => { if (alive) setAppName(name); });
    return () => { alive = false; };
  }, [api, personId, day, name]);

  if (!personId || !day || !appName) {
    return <div style={{ padding: "8px 0", fontSize: 13, color: t.c.textTertiary }}>Opening the work analysis…</div>;
  }
  return (
    <div>
      <style>{`
        @keyframes wsn-shimmer { 0% { background-position: 180% 0 } 100% { background-position: -80% 0 } }
        .wsn-shimmer {
          background-image: linear-gradient(90deg, var(--wsn-sk) 0%, var(--wsn-sk-hi) 46%, var(--wsn-sk) 100%);
          background-size: 200% 100%;
          animation: wsn-shimmer 1.35s ease-in-out infinite;
        }
      `}</style>
      <AppDetail
        key={`${personId}-${day}-${appName}`}
        app={appName}
        tone={k.blue}
        userId={personId}
        date={day}
        source="both"
        sf={sf}
        onOpenSession={onOpenSession}
      />
    </div>
  );
}

/**
 * L3 — one session / purpose, written the same way Work Summary writes a takeaway.
 * Measured facts first, then headline + timed phases. Raw capture never shown.
 */
export function WorkWriteup({ t, sf, tone, userId, date, source = "both", title, detail, blockStarts = [], threadKeys = [], compact = false }) {
  const [headline, setHeadline] = React.useState("");
  const [phases, setPhases] = React.useState([]);
  const [outcomes, setOutcomes] = React.useState([]);
  const [facts, setFacts] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const [running, setRunning] = React.useState(true);
  const [error, setError] = React.useState(null);
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return undefined;
    started.current = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/takeaway-detail`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            userId, date, source, kind: "work", title, detail,
            threadKeys, blockStarts: (blockStarts || []).map(Number).filter(Boolean),
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not open this session.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "facts") setFacts(d.facts);
            else if (d.type === "status") setStatus(d.message || "");
            else if (d.type === "headline") setHeadline(d.text);
            else if (d.type === "phase") setPhases((c) => [...c, d]);
            else if (d.type === "outcome") setOutcomes((c) => [...c, d]);
            else if (d.type === "error") setError(d.message);
            else if (d.type === "done") setRunning(false);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally { setRunning(false); }
    })();
    return () => ctrl.abort();
  }, [userId, date, source, title, detail, blockStarts, threadKeys]);

  const points = pickWriteupPoints(headline, phases);
  const k = inks(t);
  const ppr = paper(t);

  if (compact) {
    const verdicts = (outcomes || []).filter((o) => o && o.text && !points.some((p) => overlapOf(p.text, o.text) >= 0.45)).slice(0, 2);
    return (
      <div>
        {running && !headline ? <WaitLine t={t} text={status || "Writing this sitting…"} /> : null}
        {error && !headline ? <div style={{ fontSize: 13, color: t.status?.negative || t.c.textSecondary }}>{error}</div> : null}
        {!running && !error && !headline && !points.length ? (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: t.c.textSecondary }}>
            Time is real. Nothing distinct enough to name in this sitting yet.
          </p>
        ) : null}
        {headline ? (
          <p style={{ margin: "0 0 14px", fontSize: 16.5, fontWeight: 650, lineHeight: 1.35, letterSpacing: "-0.25px" }}>{headline}</p>
        ) : null}
        {verdicts.length ? (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${verdicts.length}, minmax(0,1fr))`, gap: 6, marginBottom: 14 }}>
            {verdicts.map((o, i) => {
              const open = o.state === "open";
              const ink = open ? k.gold : o.state === "progress" ? k.gold : k.blue;
              const Icon = open ? AlertTriangle : Check;
              return (
                <div key={i} style={{
                  padding: "10px 12px", borderRadius: 12,
                  background: t.isDark ? ppr.well : "#fff",
                  boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span aria-hidden="true" style={{
                      width: 22, height: 22, borderRadius: 7, display: "grid", placeItems: "center", background: ink, color: "#fff",
                    }}><Icon size={12} strokeWidth={2.4} /></span>
                    <span style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>
                      {o.state === "open" ? "Still open" : o.state === "progress" ? "In progress" : "Done"}
                    </span>
                  </span>
                  <span style={{ display: "block", marginTop: 6, fontSize: 13, fontWeight: 550, lineHeight: 1.4 }}>{o.text}</span>
                </div>
              );
            })}
          </div>
        ) : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {points.map((row, i) => {
            const Icon = insightIcon(row.kind);
            const ink = row.kind === "open" ? k.gold : k.violet;
            return (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "32px 1fr", gap: 12, alignItems: "start",
                padding: "11px 12px", borderRadius: 12,
                background: t.isDark ? ppr.well : "#fff",
                boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
              }}>
                <span aria-hidden="true" style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0, marginTop: 1,
                  display: "grid", placeItems: "center", background: ink, color: "#fff",
                }}><Icon size={15} strokeWidth={2.2} /></span>
                <span>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 550, lineHeight: 1.4, letterSpacing: "-0.15px" }}>{row.text}</span>
                  {row.who || row.time ? (
                    <span style={{ display: "block", marginTop: 4, fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                      {[row.who, row.time].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {facts ? (
        <KpiStrip t={t} items={[
          facts.totalMin != null ? { label: "Time", value: fmtDur(facts.totalMin) } : null,
          facts.windows ? { label: "Windows", value: String(facts.windows) } : null,
          facts.longestMin ? { label: "Longest", value: fmtDur(facts.longestMin) } : null,
          facts.firstMs ? { label: "When", value: `${fmtClock(facts.firstMs)}–${fmtClock(facts.lastMs)}` } : null,
        ]} />
      ) : null}

      {running && !headline ? <WaitLine t={t} text={status || "Writing this sitting…"} /> : null}
      {error && !headline ? <div style={{ fontSize: 13, color: t.status?.negative || t.c.textSecondary }}>{error}</div> : null}
      {!running && !error && !headline && !phases.length ? (
        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: t.c.textSecondary, maxWidth: "62ch" }}>
          No written analysis covers this exact window yet. The measured figures
          above are real tracked activity — the written picture appears once
          activity detail for this stretch has been analysed.
        </div>
      ) : null}
      <HeadlineBlock t={t} text={headline} />
      <Timeline t={t} phases={phases} />
      {outcomes.length ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...t.font.microLabel, color: t.c.textTertiary, margin: "12px 0 8px" }}>Where it stands</div>
          <OutcomeCards t={t} outcomes={outcomes} />
        </div>
      ) : null}
    </div>
  );
}

export default WorkWriteup;
